import moment from 'moment';
import { WEEK_DAYS } from '../shared';

const expensePickerLowerBound = moment().subtract(6, 'months');
const expensePickerUpperBound = moment().add(6, 'months');

/**
 * Gets the current date in Pacific Time (PST/PDT), automatically handling daylight saving time.
 * Only the date (year, month, day) is needed for month cutoff validation.
 * @returns {moment.Moment} Current date in Pacific Time
 */
const getPacificTimeNow = () => {
  // Use Intl API to get date components in Pacific Time
  // This automatically handles DST transitions
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  const parts = formatter.formatToParts(new Date());
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  // Create moment from Pacific Time date (time is not needed for month validation)
  return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');
};

export const isWeekday = (date) => WEEK_DAYS.includes(moment(date).day());
export const isInThisYear = (day) =>
  day.isSame(moment().startOf('month'), 'year');
export const isBeforeStartOfCurrentMonth = (day, NumberOfPreviousTimeEntryMonthsAllowed) => {
  // Use Pacific Time instead of browser local time to match backend validation
  const pacificTimeNow = getPacificTimeNow();
  let firstOpenDate = pacificTimeNow.clone().startOf('month');
  firstOpenDate = firstOpenDate.subtract(NumberOfPreviousTimeEntryMonthsAllowed, 'month');
  return day.isBefore(firstOpenDate);
};
export const getFormattedDateString = (date, format = 'MM-DD-YYYY') =>
  moment(date).format(format);
export const isTimeEntryDateValid = (day, NumberOfPreviousTimeEntryMonthsAllowed) =>
  moment.isMoment(day)
    ? !isBeforeStartOfCurrentMonth(day, NumberOfPreviousTimeEntryMonthsAllowed)
    : !isBeforeStartOfCurrentMonth(moment(day), NumberOfPreviousTimeEntryMonthsAllowed);

/** Matches NewHourInput: open window + current-or-future calendar month (bulk must not hit closed days). */
export const isDayBulkFillable = (day, numberOfPreviousMonthsAllowed) => {
  if (!isTimeEntryDateValid(day, numberOfPreviousMonthsAllowed)) return false;
  const d = moment.isMoment(day) ? day.clone() : moment(day);
  return d.isSameOrAfter(moment(), 'month');
};

/**
 * Bulk APIs iterate every calendar day in [startDate, endDate]. When the selected week straddles a closed
 * month and the current month, use only the fillable days so later days in the open month still save.
 */
export const getBulkFillDateBoundsFromSelectedDays = (selectedDays, numberOfPreviousMonthsAllowed) => {
  if (!Array.isArray(selectedDays) || selectedDays.length === 0) return null;
  const fillable = selectedDays.filter((d) => isDayBulkFillable(d, numberOfPreviousMonthsAllowed));
  if (fillable.length === 0) return null;
  const sorted = [...fillable].sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
  return {
    startDate: moment(sorted[0]).format('YYYY-MM-DD'),
    endDate: moment(sorted[sorted.length - 1]).format('YYYY-MM-DD'),
  };
};
export const isExpenseDatePickerOutsideRange = (day) =>
  !moment(day).isBetween(expensePickerLowerBound, expensePickerUpperBound);
export const getInitialActiveMonth = () => {
  const startMonth = moment().add(-2, 'months');
  const startDate = new Date(startMonth.format('YYYY'), startMonth.format('M'));
  return startDate;
};

export const getWeekDays = (weekStart) => {
  const days = [weekStart];
  for (let i = 1; i < 7; i += 1) {
    days.push(moment(weekStart).add(i, 'days').toDate());
  }
  return days;
};

export const getAddButtonEnabled = (firstDay) => {
  const lastDay = moment(firstDay).add(6, 'days').format('YYYY-MM-DD');
  if (moment(lastDay).isSameOrAfter(moment(), 'month')) {
    return true;
  }
  return false;
}

export const getWeekRange = (date) => ({
  from: date.startOf('week').toDate(),
  to: date.endOf('week').toDate(),
});

export const getCurrentDateParts = () => ({
  currentMonth: moment().month(),
  currentYear: moment().year(),
});
